import type { ReactNode } from "react";

type Tone = "neutral" | "pos" | "warn" | "neg";

const ICONS: Record<Tone, ReactNode> = {
  neutral: <circle cx="12" cy="12" r="9" />,
  pos: <><circle cx="12" cy="12" r="9" /><path d="m8.5 12.5 2.5 2.5 4.5-5" /></>,
  warn: <><path d="M12 4 2.5 20h19z" /><path d="M12 10v4M12 17h.01" /></>,
  neg: <><circle cx="12" cy="12" r="9" /><path d="M12 8v4.5M12 16h.01" /></>,
};

export function Banner({ tone = "neutral", title, children, action }: {
  tone?: Tone; title?: ReactNode; children?: ReactNode; action?: ReactNode;
}) {
  return (
    <div className={["banner", tone !== "neutral" && `banner-${tone}`].filter(Boolean).join(" ")}
         role={tone === "neg" ? "alert" : "status"}>
      <svg className="banner-icon" width="17" height="17" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        {ICONS[tone]}
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <strong className="banner-title">{title}</strong>}
        {children}
      </div>
      {action}
    </div>
  );
}

export function Progress({ value, max = 100, label }: {
  value: number; max?: number; label?: string;
}) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className="progress" role="progressbar" aria-valuenow={value}
         aria-valuemin={0} aria-valuemax={max} aria-label={label}>
      <div className="progress-bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Skeleton({ width = "100%", height = 14, radius }: {
  width?: number | string; height?: number | string; radius?: number;
}) {
  return <div className="skeleton" aria-hidden="true"
              style={{ width, height, borderRadius: radius }} />;
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <span role="status">
      <span className="spinner" />
      <span className="sr-only">{label}</span>
    </span>
  );
}
